import React, { useEffect, useRef, useState } from "react";
import "./table.less";
import { Button, Dropdown } from "../../BP2Components";
import { useIntl } from "react-intl";
import { DROPDOWN_ITEM_SELECTOR } from "../../../util/ListGridNavWrapper";
import { findPopup, focusNext } from "../../ActionsDropdown/ActionsDropdown";

const TableActions = ({ actions = [], cellData, onCustomEvent, rowIndex }) => {
   const intl = useIntl();
   const [isDropdownOpen, setIsDropdownOpen] = useState(false);
   const justClosedAtRef = useRef(0);
   const toggleRef = React.useRef(null);
   const rowActions = Array.isArray(actions?.[rowIndex]) ? actions[rowIndex] : actions;
   const dropdownItems =
      rowActions?.map((action) => ({
         id: action.name,
         name: action.name,
         onClick: () => {
            onCustomEvent({ type: "Action", data: action, rowData: cellData });
         },
      })) ?? [];
   const hasModalShadow = () => !!document.querySelector(".modal-shadow");

   const setOpen = (open) => {
      setIsDropdownOpen(!!open);
      if (!open) justClosedAtRef.current = Date.now();
   };
   const portalHostRef = React.useRef(null);
   const handleToggleClick = (e) => {
      // ignore click from inside dropdown
      if (portalHostRef.current && portalHostRef.current.contains(e.target)) return;
      if (e.target !== e.currentTarget) return;

      // don't open when modal is open
      if (hasModalShadow()) {
         e.preventDefault();
         return;
      }

      // short debounce after close
      if (Date.now() - justClosedAtRef.current < 250) return;

      setIsDropdownOpen((val) => !val);
   };

   const stop = (e) => e.stopPropagation();
   useEffect(() => {
      if (!isDropdownOpen) return;

      const onKeyCapture = (e) => {
         const popup = findPopup();
         if (!popup) return;

         const inPopup =
            popup.contains(e.target) || popup.contains(document.activeElement || document.body);
         if (!inPopup) return;

         const stopAll = () => {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation?.();
         };

         if (e.key === "Escape") {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation?.();

            try {
               setIsDropdownOpen(false);
            } catch {}

            requestAnimationFrame(() => {
               requestAnimationFrame(() => {
                  try {
                     const active = document.activeElement;
                     if (active && popup.contains(active) && typeof active.blur === "function") {
                        active.blur();
                     }
                  } catch {}

                  try {
                     const ownerRoot =
                        document.querySelector(
                           '[data-table-widget="true"][data-bp2-grid-active="true"]',
                        ) ||
                        (document.querySelector(".bp2-active-cell")
                           ? document
                                .querySelector(".bp2-active-cell")
                                .closest?.('[data-table-widget="true"]')
                           : null);

                     const target =
                        ownerRoot?.querySelector('[data-grid-cell="true"][tabindex="0"]') ||
                        ownerRoot?.querySelector('[data-grid-cell="true"]') ||
                        ownerRoot;

                     target?.focus?.({ preventScroll: true });
                  } catch {}
               });
            });

            return;
         }

         if (e.key === "ArrowDown") {
            stopAll();
            focusNext(popup, +1);
            return;
         }
         if (e.key === "ArrowUp") {
            stopAll();
            focusNext(popup, -1);
            return;
         }
         if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
            stopAll();
            return;
         }

         if (e.key === "Tab") {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation?.();

            const dir = e.shiftKey ? -1 : +1;

            try {
               setIsDropdownOpen(false);
            } catch {}

            // after close dropdown - change table
            let tries = 0;
            const hop = () => {
               const popupStillThere = (() => {
                  const root = document.getElementById("dropdown-root");
                  if (!root) return false;
                  return !!(
                     root.querySelector(".items-container") ||
                     root.querySelector('[role="menu"]') ||
                     root.querySelector("[data-bp2-dropdown]")
                  );
               })();

               if (popupStillThere && tries++ < 60) {
                  return requestAnimationFrame(hop);
               }

               const ownerRoot =
                  (toggleRef.current &&
                     toggleRef.current.closest?.('[data-table-widget="true"]')) ||
                  document.querySelector(
                     '[data-table-widget="true"][data-bp2-grid-active="true"]',
                  ) ||
                  (document.querySelector(".bp2-active-cell")
                     ? document
                          .querySelector(".bp2-active-cell")
                          .closest('[data-table-widget="true"]')
                     : null);

               const tables = Array.from(document.querySelectorAll('[data-table-widget="true"]'));
               const idx = ownerRoot ? tables.indexOf(ownerRoot) : -1;

               // set index destination table
               let targetIdx;
               if (idx === -1) targetIdx = 0;
               else targetIdx = (idx + dir + tables.length) % tables.length;

               const nextTable = tables[targetIdx];

               // clear focus
               try {
                  const prevActive = ownerRoot?.querySelector(
                     '[data-grid-cell="true"][tabindex="0"]',
                  );
                  if (prevActive) {
                     prevActive.setAttribute("tabindex", "-1");
                     prevActive.blur?.();
                  }
                  ownerRoot
                     ?.querySelectorAll(".bp2-active-cell")
                     ?.forEach((el) => el.classList.remove("bp2-active-cell"));
                  toggleRef.current?.blur?.();
               } catch {}

               // 5) focus cell in new table
               const targetCell =
                  nextTable?.querySelector('[data-grid-cell="true"][tabindex="0"]') ||
                  nextTable?.querySelector('[data-grid-cell="true"]') ||
                  nextTable;

               targetCell?.focus?.({ preventScroll: true });
            };
            requestAnimationFrame(hop);
            return;
         }
      };

      window.addEventListener("keydown", onKeyCapture, true);
      return () => window.removeEventListener("keydown", onKeyCapture, true);
   }, [isDropdownOpen]);

   useEffect(() => {
      if (!isDropdownOpen) return;

      let tries = 0,
         cancelled = false;
      const pump = () => {
         if (cancelled) return;
         const popup = findPopup();
         if (popup) {
            if (!popup.hasAttribute("tabindex")) popup.setAttribute("tabindex", "-1");
            const items = Array.from(popup.querySelectorAll(DROPDOWN_ITEM_SELECTOR)).filter(
               (el) => !el.disabled && el.tabIndex !== -1,
            );
            (items[0] ?? popup).focus({ preventScroll: true });
            return;
         }
         if (++tries < 80) requestAnimationFrame(pump);
      };

      requestAnimationFrame(pump);
      return () => {
         cancelled = true;
      };
   }, [isDropdownOpen]);

   return (
      <>
         {rowActions?.length === 1 ? (
            <Button
               buttonType="primary"
               size="regular"
               className="action-button list-service-action"
               onClick={() =>
                  onCustomEvent({ type: "Action", data: rowActions[0], rowData: cellData })
               }
            >
               {rowActions[0]?.name}
            </Button>
         ) : (
            <Button
               ref={toggleRef}
               size="regular"
               buttonType="primary"
               className="list-service-actions-dropdown-button"
               onClick={handleToggleClick}
               aria-haspopup="menu"
               aria-expanded={isDropdownOpen ? "true" : "false"}
            >
               {intl.formatMessage({ id: "table.Actions" })}
               <span
                  ref={portalHostRef}
                  onMouseDown={stop}
                  onMouseUp={stop}
                  onClick={stop}
                  onKeyDown={stop}
                  onKeyUp={stop}
               >
                  <Dropdown
                     items={dropdownItems}
                     isOpen={isDropdownOpen}
                     setIsOpen={setOpen}
                     customZIndex={1001}
                  />
               </span>
            </Button>
         )}
      </>
   );
};

export default TableActions;
